import React, { useState, useEffect, useMemo, useCallback } from 'react';
import axiosConfig from '../../../../api/axiosConfig';
import { 
  FiUsers, FiDollarSign, FiCalendar, FiTrendingUp, FiDownload, 
  FiPrinter, FiEye, FiFilter, FiSearch, FiX, FiChevronDown,
  FiChevronUp, FiPieChart, FiBarChart2, FiFileText, FiMail,
  FiClock, FiCheckCircle, FiXCircle, FiBriefcase, FiActivity
} from 'react-icons/fi';
import * as XLSX from 'xlsx';

const CompaniesReportPage = () => {
  const [sirketler, setSirketler] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedPeriod, setSelectedPeriod] = useState('yearly');
  const [selectedSirket, setSelectedSirket] = useState(null);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [reportType, setReportType] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [sortConfig, setSortConfig] = useState({ key: 'toplamAlacak', direction: 'desc' });
  const [showGraphs, setShowGraphs] = useState(false);
  const [expandedRows, setExpandedRows] = useState(new Set());
  const [selectedSirketler, setSelectedSirketler] = useState(new Set());
  const [dateRange, setDateRange] = useState({ start: '', end: '' });
  const [customDateMode, setCustomDateMode] = useState(false);
  
  // Cache i?in state'ler
  const [derslerCache, setDerslerCache] = useState([]);
  const [kursSatislarCache, setKursSatislarCache] = useState([]);
  const [ogrencilerCache, setOgrencilerCache] = useState([]);

  // Helper fonksiyonlari
  const getSelectedPeriodAmount = useCallback((sirket) => {
    if (!sirket) return 0;
    if (customDateMode) return sirket.customAlacak || 0;
    switch (selectedPeriod) {
      case 'daily': return sirket.gunlukAlacak || 0;
      case 'weekly': return sirket.haftalikAlacak || 0;
      case 'current': return sirket.aylikAlacak || 0;
      case 'yearly': return sirket.yillikAlacak || 0;
      default: return 0;
    }
  }, [selectedPeriod, customDateMode]);

  const getSelectedPeriodDetails = useCallback((sirket) => {
    if (!sirket) return [];
    if (customDateMode) return sirket.customDersDetaylari || [];
    switch (selectedPeriod) {
      case 'daily': return sirket.gunlukDersDetaylari || [];
      case 'weekly': return sirket.haftalikDersDetaylari || [];
      case 'current': return sirket.dersDetaylari || [];
      case 'yearly': return sirket.yillikDersDetaylari || [];
      default: return [];
    }
  }, [selectedPeriod, customDateMode]);

  useEffect(() => {
    fetchInitialData();
  }, []);

  useEffect(() => {
    if (dateRange.start && dateRange.end && sirketler.length > 0) {
      updateCustomDateData();
    }
  }, [dateRange]);

  // Dersleri tarihe g?re gruplama
  const groupDerslerByDate = (dersler) => {
    const currentDate = new Date();
    const currentMonth = currentDate.getMonth() + 1;
    const currentYear = currentDate.getFullYear();
    
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const tomorrow = new Date(today);
    tomorrow.setDate(tomorrow.getDate() + 1);
    
    const weekStart = new Date(currentDate);
    weekStart.setDate(currentDate.getDate() - currentDate.getDay());
    weekStart.setHours(0, 0, 0, 0);
    const weekEnd = new Date(weekStart);
    weekEnd.setDate(weekStart.getDate() + 7);

    return {
      monthly: dersler.filter(ders => {
        const dersDate = new Date(ders.baslangic_tarihi);
        return dersDate.getMonth() + 1 === currentMonth && 
               dersDate.getFullYear() === currentYear;
      }),
      daily: dersler.filter(ders => {
        const dersDate = new Date(ders.baslangic_tarihi);
        return dersDate >= today && dersDate < tomorrow;
      }),
      weekly: dersler.filter(ders => {
        const dersDate = new Date(ders.baslangic_tarihi);
        return dersDate >= weekStart && dersDate < weekEnd;
      }),
      yearly: dersler
    };
  };

  // D?nem bazli ?deme hesaplama - ?-GRENCI ?oZERINDEN
  const calculatePeriodPayment = (sirketId, ogrenciler, periodDersler) => {
    let toplam40 = 0, toplam60 = 0, toplam80 = 0, toplam120 = 0;
    let toplamUcret = 0;
    const dersDetaylari = [];

    // Sirkete kayitli ??Yrencilerin ID'lerini al
    const sirketOgrenciIds = ogrenciler
      .filter(o => String(o.registered_system) === String(sirketId))
      .map(o => o.id);

    // Bu ??Yrencilerin derslerini i?Yle
    periodDersler.forEach(ders => {
      // Ders ?Yirkete kayitli bir ??Yrenciye ait mi kontrol et
      if (!sirketOgrenciIds.includes(ders.ogrenci_id)) return;

      const sirketPayi = parseFloat(ders.sirket_payi) || 0;
      
      if (sirketPayi === 0) return;

      const dersSure = parseInt(ders.ders_sure);
      if (dersSure == 40) toplam40 += sirketPayi;
      if (dersSure == 60) toplam60 += sirketPayi;
      if (dersSure == 80) toplam80 += sirketPayi;
      if (dersSure == 120) toplam120 += sirketPayi;

      toplamUcret += sirketPayi;

      dersDetaylari.push({
        id: ders.id,
        tarih: ders.baslangic_tarihi,
        konu: ders.ders_konusu,
        sure: ders.ders_sure,
        ucret: sirketPayi,
        ogrenciId: ders.ogrenci_id,
        ogretmenId: ders.ogretmen_id,
        kursId: ders.kurs_id,
        ogrenciAdi: ders.ogrenci_name || ders.ogrenci_adi || '-',
        ogretmenAdi: ders.ogretmen_name || ders.ogretmen_adi || '-',
        ogrenciTelefon: ders.parent_phone || '-',
        ogretmenTelefon: ders.ogretmen_telefon || '-'
      });
    });

    return { toplamUcret, toplam40, toplam60, toplam80, toplam120, dersDetaylari };
  };

  // Sirket verilerini i?Yleme - ?-GRENCI ?oZERINDEN
  const processSirketlerData = useCallback((sirketlerData, dersler, kursSatislar, ogrenciler) => {
    // Dersleri tarihe g?re grupla
    const derslerByDate = groupDerslerByDate(dersler);

    return sirketlerData.map(sirket => {
      // Sirkete kayitli ??Yrencileri bul
      const sirketOgrencileri = ogrenciler.filter(o => 
        String(o.registered_system) === String(sirket.id)
      );

      // Aktif ve pasif ??Yrenci sayilari
      const aktifOgrenciSayisi = sirketOgrencileri.filter(o => String(o.status) === '1').length;
      const pasifOgrenciSayisi = sirketOgrencileri.filter(o => String(o.status) === '0').length;

      // Kurs sat?"??.Y say?"?s?"?n?"? backend verisinden hesapla
      const sirketKurslari = kursSatislar.filter((kurs) => {
        const courseCompanyId = kurs.sirket_id ?? (
          /^\d+$/.test(String(kurs.sirket_adi || '')) ? kurs.sirket_adi : null
        );
        return String(courseCompanyId) === String(sirket.id);
      });

      // Her d?nem i?in hesaplama yap - ??Yrenciler ?zerinden
      const aylikData = calculatePeriodPayment(sirket.id, ogrenciler, derslerByDate.monthly);
      const gunlukData = calculatePeriodPayment(sirket.id, ogrenciler, derslerByDate.daily);
      const haftalikData = calculatePeriodPayment(sirket.id, ogrenciler, derslerByDate.weekly);
      const yillikData = calculatePeriodPayment(sirket.id, ogrenciler, derslerByDate.yearly);

      return {
        ...sirket,
        aylikAlacak: aylikData.toplamUcret,
        gunlukAlacak: gunlukData.toplamUcret,
        haftalikAlacak: haftalikData.toplamUcret,
        yillikAlacak: yillikData.toplamUcret,
        toplam40: aylikData.toplam40,
        toplam60: aylikData.toplam60,
        toplam80: aylikData.toplam80,
        toplam120: aylikData.toplam120,
        dersDetaylari: aylikData.dersDetaylari,
        gunlukDersDetaylari: gunlukData.dersDetaylari,
        haftalikDersDetaylari: haftalikData.dersDetaylari,
        yillikDersDetaylari: yillikData.dersDetaylari,
        toplamDersSayisi: yillikData.dersDetaylari.length,
        kursSayisi: sirketKurslari.length,
        ogrenciSayisi: sirketOgrencileri.length,
        aktifOgrenciSayisi,
        pasifOgrenciSayisi
      };
    });
  }, [calculatePeriodPayment]);

  // Paralel veri ?ekme
  const fetchInitialData = async () => {
    setLoading(true);
    try {
      // T?m API ?a?Yrilarini paralel yap
      const [sirketRes, derslerRes, kursSatisRes, ogrenciRes] = await Promise.all([
        axiosConfig.get('/admin/sirkets'),
        axiosConfig.get('/admin/ogretmenders'),
        axiosConfig.get('/admin/kurssatis'),
        axiosConfig.get('/admin/ogrencis')
      ]);

      const sirketlerData = sirketRes.data || [];
      const dersler = Array.isArray(derslerRes.data) ? derslerRes.data : (derslerRes.data.data || []);
      const kursSatislar = Array.isArray(kursSatisRes.data) ? kursSatisRes.data : (kursSatisRes.data.data || []);
      const ogrenciler = Array.isArray(ogrenciRes.data) ? ogrenciRes.data : (ogrenciRes.data.data || []);

      // Cache'e kaydet
      setDerslerCache(dersler);
      setKursSatislarCache(kursSatislar);
      setOgrencilerCache(ogrenciler);

      console.log('Toplam şirket sayısı:', sirketlerData.length);
      console.log('Toplam ders sayisi:', dersler.length);
      console.log('Toplam kurs satış sayısı:', kursSatislar.length);
      
      // Debug i?in ?rnek veriler
      console.log('Örnek kurs satışı:', kursSatislar[0]);
      console.log('Örnek ders:', dersler[0]);
      console.log('Örnek şirket:', sirketlerData[0]);

      // Sirket verilerini i?Yle
      const processedSirketler = processSirketlerData(sirketlerData, dersler, kursSatislar, ogrenciler);
      setSirketler(processedSirketler);
    } catch (error) {
      console.error('Veri yükleme hatası:', error);
      alert('Veri yüklenirken hata oluştu!');
    } finally {
      setLoading(false);
    }
  };

  // ?-zel tarih arali?Yi i?in veri g?ncelleme
  const updateCustomDateData = useCallback(() => {
    const startDate = new Date(dateRange.start);
    const endDate = new Date(dateRange.end);
    endDate.setHours(23, 59, 59, 999);

    const customDersler = derslerCache.filter(ders => {
      const dersDate = new Date(ders.baslangic_tarihi);
      return dersDate >= startDate && dersDate <= endDate;
    });

    const updatedSirketler = sirketler.map(sirket => {
      const customData = calculatePeriodPayment(sirket.id, ogrencilerCache, customDersler);
      
      return {
        ...sirket,
        customAlacak: customData.toplamUcret,
        customDersDetaylari: customData.dersDetaylari
      };
    });

    setSirketler(updatedSirketler);
  }, [dateRange, derslerCache, ogrencilerCache, sirketler, calculatePeriodPayment]);

  // Siralama fonksiyonu
  const handleSort = useCallback((key) => {
    setSortConfig(prevConfig => ({
      key,
      direction: prevConfig.key === key && prevConfig.direction === 'asc' ? 'desc' : 'asc'
    }));
  }, []);

  // Memoized summary stats
  const summaryStats = useMemo(() => {
    let filteredSirketler = [...sirketler];

    // Arama filtresi
    if (searchTerm) {
      filteredSirketler = filteredSirketler.filter(s => 
        s.sirket_adi.toLowerCase().includes(searchTerm.toLowerCase()) ||
        String(s.id).includes(searchTerm)
      );
    }

    const selectedPeriodStats = {
      toplam40: 0,
      toplam60: 0,
      toplam80: 0,
      toplam120: 0
    };

    filteredSirketler.forEach(sirket => {
      if (selectedPeriod === 'current' && !customDateMode) {
        selectedPeriodStats.toplam40 += sirket.toplam40 || 0;
        selectedPeriodStats.toplam60 += sirket.toplam60 || 0;
        selectedPeriodStats.toplam80 += sirket.toplam80 || 0;
        selectedPeriodStats.toplam120 += sirket.toplam120 || 0;
      } else {
        const periodDetails = getSelectedPeriodDetails(sirket);
        periodDetails.forEach(ders => {
          const dersSure = parseInt(ders.sure);
          const ucret = ders.ucret;
          switch(dersSure) {
            case 40: selectedPeriodStats.toplam40 += ucret; break;
            case 60: selectedPeriodStats.toplam60 += ucret; break;
            case 80: selectedPeriodStats.toplam80 += ucret; break;
            case 120: selectedPeriodStats.toplam120 += ucret; break;
          }
        });
      }
    });

    if (reportType === 'with-payment') {
      filteredSirketler = filteredSirketler.filter(s => getSelectedPeriodAmount(s) > 0);
    } else if (reportType === 'no-payment') {
      filteredSirketler = filteredSirketler.filter(s => getSelectedPeriodAmount(s) === 0);
    }

    const summary = filteredSirketler.reduce((acc, sirket) => {
      const amount = getSelectedPeriodAmount(sirket);
      
      acc.toplamSirket++;
      if (amount > 0) acc.alacakliSirket++;
      else acc.alacaksizSirket++;
      
      acc.toplamAlacak += amount;
      acc.toplamOgrenci += sirket.ogrenciSayisi || 0;
      acc.toplamAktifOgrenci += sirket.aktifOgrenciSayisi || 0;
      acc.toplamPasifOgrenci += sirket.pasifOgrenciSayisi || 0;
      acc.toplamKurs += sirket.kursSayisi || 0;
      
      return acc;
    }, {
      toplamSirket: 0,
      alacakliSirket: 0,
      alacaksizSirket: 0,
      toplamAlacak: 0,
      toplamOgrenci: 0,
      toplamAktifOgrenci: 0,
      toplamPasifOgrenci: 0,
      toplamKurs: 0,
      toplam40: selectedPeriodStats.toplam40,
      toplam60: selectedPeriodStats.toplam60,
      toplam80: selectedPeriodStats.toplam80,
      toplam120: selectedPeriodStats.toplam120
    });

    return summary;
  }, [sirketler, selectedPeriod, reportType, searchTerm, customDateMode, getSelectedPeriodAmount, getSelectedPeriodDetails]);

  // Memoized filtered sirketler
  const filteredSirketler = useMemo(() => {
    let filtered = [...sirketler];

    // Arama filtresi
    if (searchTerm) {
      filtered = filtered.filter(s => 
        s.sirket_adi.toLowerCase().includes(searchTerm.toLowerCase()) ||
        String(s.id).includes(searchTerm)
      );
    }

    if (reportType === 'with-payment') {
      filtered = filtered.filter(s => getSelectedPeriodAmount(s) > 0);
    } else if (reportType === 'no-payment') {
      filtered = filtered.filter(s => getSelectedPeriodAmount(s) === 0);
    }

    // Siralama
    filtered.sort((a, b) => {
      let aValue, bValue;
      
      switch(sortConfig.key) {
        case 'sirket_adi':
          aValue = a.sirket_adi;
          bValue = b.sirket_adi;
          break;
        case 'toplamAlacak':
          aValue = getSelectedPeriodAmount(a);
          bValue = getSelectedPeriodAmount(b);
          break;
        case 'dersSayisi':
          aValue = getSelectedPeriodDetails(a).length;
          bValue = getSelectedPeriodDetails(b).length;
          break;
        case 'ogrenciSayisi':
          aValue = a.ogrenciSayisi || 0;
          bValue = b.ogrenciSayisi || 0;
          break;
        case 'kursSayisi':
          aValue = a.kursSayisi || 0;
          bValue = b.kursSayisi || 0;
          break;
        default:
          aValue = a[sortConfig.key];
          bValue = b[sortConfig.key];
      }

      if (aValue < bValue) return sortConfig.direction === 'asc' ? -1 : 1;
      if (aValue > bValue) return sortConfig.direction === 'asc' ? 1 : -1;
      return 0;
    });

    return filtered;
  }, [sirketler, reportType, searchTerm, sortConfig, getSelectedPeriodAmount, getSelectedPeriodDetails]);

  const formatCurrency = useCallback((amount) => {
    return new Intl.NumberFormat('tr-TR', {
      style: 'currency',
      currency: 'TRY',
      minimumFractionDigits: 2
    }).format(amount || 0);
  }, []);

  const formatDate = useCallback((dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('tr-TR');
  }, []);

  const handleSirketClick = useCallback((sirket) => {
    setSelectedSirket(sirket);
    setShowDetailsModal(true);
  }, []);

  const toggleRowExpansion = useCallback((sirketId) => {
    setExpandedRows(prev => {
      const newSet = new Set(prev);
      if (newSet.has(sirketId)) {
        newSet.delete(sirketId);
      } else {
        newSet.add(sirketId);
      }
      return newSet;
    });
  }, []);

  const toggleSirketSelection = useCallback((sirketId) => {
    setSelectedSirketler(prev => {
      const newSet = new Set(prev);
      if (newSet.has(sirketId)) {
        newSet.delete(sirketId);
      } else {
        newSet.add(sirketId);
      }
      return newSet;
    });
  }, []);

  const selectAllSirketler = useCallback(() => {
    if (selectedSirketler.size === filteredSirketler.length) {
      setSelectedSirketler(new Set());
    } else {
      setSelectedSirketler(new Set(filteredSirketler.map(s => s.id)));
    }
  }, [filteredSirketler, selectedSirketler]);

  const exportToExcel = useCallback(() => {
    const dataToExport = filteredSirketler.map(sirket => {
      const periodAmount = getSelectedPeriodAmount(sirket);
      const periodDetails = getSelectedPeriodDetails(sirket);
      
      return {
        'ID': sirket.id,
        'Şirket Adı': sirket.sirket_adi,
        'Öğrenci Sayısı': sirket.ogrenciSayisi || 0,
        'Aktif Öğrenci': sirket.aktifOgrenciSayisi || 0,
        'Pasif Öğrenci': sirket.pasifOgrenciSayisi || 0,
        'Kurs Sayısı': sirket.kursSayisi || 0,
        'Ders Sayısı': periodDetails.length,
        'Alacak Tutarı': periodAmount,
        'Durum': periodAmount > 0 ? 'Alacaklı' : 'Alacak Yok',
        '40 Dk Toplam': sirket.toplam40 || 0,
        '60 Dk Toplam': sirket.toplam60 || 0,
        '80 Dk Toplam': sirket.toplam80 || 0,
        '120 Dk Toplam': sirket.toplam120 || 0
      };
    });

    const ws = XLSX.utils.json_to_sheet(dataToExport);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Şirket Raporu");
    
    const fileName = `sirket_raporu_${new Date().toISOString().split('T')[0]}.xlsx`;
    XLSX.writeFile(wb, fileName);
  }, [filteredSirketler, getSelectedPeriodAmount, getSelectedPeriodDetails]);

  const exportSelectedToExcel = useCallback(() => {
    const selectedData = filteredSirketler.filter(s => selectedSirketler.has(s.id));
    
    const detailedData = [];
    selectedData.forEach(sirket => {
      const periodDetails = getSelectedPeriodDetails(sirket);
      periodDetails.forEach(ders => {
        detailedData.push({
          'Şirket ID': sirket.id,
          'Şirket Adı': sirket.sirket_adi,
          'Ders ID': ders.id,
          'Ders Tarihi': formatDate(ders.tarih),
          'Ders Konusu': ders.konu || '-',
          'Ders Süresi': `${ders.sure} dk`,
          'Şirket Payı': ders.ucret
        });
      });
    });

    const ws = XLSX.utils.json_to_sheet(detailedData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Detaylı Ders Raporu");
    
    const fileName = `sirket_detayli_rapor_${new Date().toISOString().split('T')[0]}.xlsx`;
    XLSX.writeFile(wb, fileName);
  }, [filteredSirketler, selectedSirketler, getSelectedPeriodDetails, formatDate]);

  const sendBulkEmail = useCallback(() => {
    const selectedData = filteredSirketler.filter(s => selectedSirketler.has(s.id));
    alert(`${selectedData.length} şirkete toplu email gönderilecek. (Bu özellik henüz aktif değil)`);
  }, [filteredSirketler, selectedSirketler]);

  const printReport = useCallback(() => {
    window.print();
  }, []);

  const getPeriodText = useCallback(() => {
    if (customDateMode && dateRange.start && dateRange.end) {
      return `${formatDate(dateRange.start)} - ${formatDate(dateRange.end)}`;
    }
    const periodTexts = {
      'current': 'Bu Ay',
      'daily': 'Bugün',
      'weekly': 'Bu Hafta',
      'yearly': 'Tüm Zamanlar'
    };
    return periodTexts[selectedPeriod] || 'Tümü';
  }, [selectedPeriod, customDateMode, dateRange, formatDate]);

  const getGraphData = useMemo(() => {
    const top10 = filteredSirketler
      .slice(0, 10)
      .map(s => ({
        name: s.sirket_adi,
        value: getSelectedPeriodAmount(s)
      }));

    const durationData = [
      { name: '40 Dakika', value: summaryStats.toplam40 },
      { name: '60 Dakika', value: summaryStats.toplam60 },
      { name: '80 Dakika', value: summaryStats.toplam80 },
      { name: '120 Dakika', value: summaryStats.toplam120 }
    ];

    return { top10, durationData };
  }, [filteredSirketler, summaryStats, getSelectedPeriodAmount]);

  return (
    <div className="min-h-screen bg-gray-50 p-4 md:p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="bg-white rounded-xl shadow-sm p-6 mb-6">
          <div className="flex justify-between items-center">
            <div>
              <h1 className="text-2xl font-bold text-gray-800">Şirketler Raporu</h1>
              <p className="text-gray-600 mt-1">Şirketlerin kurs ve ödeme durumları</p>
            </div>
            <div className="flex gap-3">
              <button
                onClick={() => setShowGraphs(!showGraphs)}
                className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors flex items-center gap-2"
              >
                {showGraphs ? <FiPieChart className="w-5 h-5" /> : <FiBarChart2 className="w-5 h-5" />}
                {showGraphs ? 'Grafikler' : 'Tablo'}
              </button>
              <button
                onClick={printReport}
                className="px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors flex items-center gap-2"
              >
                <FiPrinter className="w-5 h-5" />
                Yazdır
              </button>
              <button
                onClick={exportToExcel}
                className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors flex items-center gap-2"
              >
                <FiDownload className="w-5 h-5" />
                Excel İndir
              </button>
            </div>
          </div>
        </div>

        {/* Filters */}
        <div className="bg-white rounded-xl shadow-sm p-6 mb-6">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {/* Period Filter */}
            <div className="lg:col-span-2">
              <h3 className="text-sm font-semibold text-gray-700 mb-3">Dönem Seçimi</h3>
              <div className="flex flex-wrap gap-2">
                {[
                  { value: 'daily', label: 'Günlük', icon: FiClock },
                  { value: 'weekly', label: 'Haftalık', icon: FiCalendar },
                  { value: 'current', label: 'Aylık', icon: FiCalendar },
                  { value: 'yearly', label: 'Tüm Zamanlar', icon: FiTrendingUp }
                ].map(period => (
                  <button
                    key={period.value}
                    onClick={() => {
                      setSelectedPeriod(period.value);
                      setCustomDateMode(false);
                    }}
                    className={`px-4 py-2 rounded-lg font-medium transition-colors flex items-center gap-2 ${
                      selectedPeriod === period.value && !customDateMode
                        ? 'bg-indigo-600 text-white'
                        : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                    }`}
                  >
                    <period.icon className="w-4 h-4" />
                    {period.label}
                  </button>
                ))}
                <button
                  onClick={() => setCustomDateMode(!customDateMode)}
                  className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                    customDateMode
                      ? 'bg-purple-600 text-white'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  Özel Tarih
                </button>
              </div>
              
              {customDateMode && (
                <div className="mt-3 flex gap-3 items-center">
                  <input
                    type="date"
                    value={dateRange.start}
                    onChange={(e) => setDateRange(prev => ({ ...prev, start: e.target.value }))}
                    className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                  />
                  <span className="text-gray-500">-</span>
                  <input
                    type="date"
                    value={dateRange.end}
                    onChange={(e) => setDateRange(prev => ({ ...prev, end: e.target.value }))}
                    className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                  />
                </div>
              )}
            </div>

            {/* Search */}
            <div>
              <h3 className="text-sm font-semibold text-gray-700 mb-3">Arama</h3>
              <div className="relative">
                <FiSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                <input
                  type="text"
                  placeholder="Şirket adı veya ID ile ara..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-10 pr-10 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                />
                {searchTerm && (
                  <button
                    onClick={() => setSearchTerm('')}
                    className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                  >
                    <FiX className="w-5 h-5" />
                  </button>
                )}
              </div>
            </div>

            {/* Report Type Filter */}
            <div className="lg:col-span-3">
              <h3 className="text-sm font-semibold text-gray-700 mb-3">Rapor Tipi</h3>
              <div className="flex gap-2">
                {[
                  { value: 'all', label: 'Tüm Şirketler', icon: FiBriefcase },
                  { value: 'with-payment', label: 'Alacağı Olanlar', icon: FiCheckCircle },
                  { value: 'no-payment', label: 'Alacağı Olmayanlar', icon: FiXCircle }
                ].map(type => (
                  <button
                    key={type.value}
                    onClick={() => setReportType(type.value)}
                    className={`px-4 py-2 rounded-lg font-medium transition-colors flex items-center gap-2 ${
                      reportType === type.value
                        ? 'bg-purple-600 text-white'
                        : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                    }`}
                  >
                    <type.icon className="w-4 h-4" />
                    {type.label}
                  </button>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* Summary Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
          <div className="bg-white rounded-lg shadow-sm p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Toplam Şirket</p>
                <p className="text-2xl font-bold text-gray-800">{summaryStats.toplamSirket}</p>
                <p className="text-xs text-gray-500 mt-1">
                  {summaryStats.toplamKurs} kurs
                </p>
              </div>
              <div className="p-3 bg-blue-100 rounded-lg">
                <FiBriefcase className="w-6 h-6 text-blue-600" />
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow-sm p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Toplam Öğrenci</p>
                <p className="text-2xl font-bold text-indigo-600">{summaryStats.toplamOgrenci}</p>
                <p className="text-xs text-gray-500 mt-1">
                  {summaryStats.toplamAktifOgrenci} aktif / {summaryStats.toplamPasifOgrenci} pasif
                </p>
              </div>
              <div className="p-3 bg-indigo-100 rounded-lg">
                <FiUsers className="w-6 h-6 text-indigo-600" />
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow-sm p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Alacaklı Şirket</p>
                <p className="text-2xl font-bold text-green-600">{summaryStats.alacakliSirket}</p>
                <p className="text-xs text-gray-500 mt-1">
                  %{summaryStats.toplamSirket > 0 ? ((summaryStats.alacakliSirket / summaryStats.toplamSirket) * 100).toFixed(1) : 0}
                </p>
              </div>
              <div className="p-3 bg-green-100 rounded-lg">
                <FiTrendingUp className="w-6 h-6 text-green-600" />
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow-sm p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Toplam Alacak</p>
                <p className="text-xl font-bold text-purple-600">{formatCurrency(summaryStats.toplamAlacak)}</p>
                <p className="text-xs text-gray-500 mt-1">
                  {getPeriodText()}
                </p>
              </div>
              <div className="p-3 bg-purple-100 rounded-lg">
                <FiDollarSign className="w-6 h-6 text-purple-600" />
              </div>
            </div>
          </div>
        </div>

        {/* Ders Süre Dağılımı */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
          <div className="bg-gradient-to-r from-blue-500 to-blue-600 rounded-lg shadow-sm p-4 text-white">
            <div className="flex justify-between items-start">
              <div>
                <p className="text-sm opacity-90">40 Dakika</p>
                <p className="text-2xl font-bold">{formatCurrency(summaryStats.toplam40)}</p>
              </div>
              <div className="text-right">
                <p className="text-2xl font-bold opacity-90">
                  %{summaryStats.toplamAlacak > 0 ? ((summaryStats.toplam40 / summaryStats.toplamAlacak) * 100).toFixed(0) : 0}
                </p>
              </div>
            </div>
          </div>
          <div className="bg-gradient-to-r from-purple-500 to-purple-600 rounded-lg shadow-sm p-4 text-white">
            <div className="flex justify-between items-start">
              <div>
                <p className="text-sm opacity-90">60 Dakika</p>
                <p className="text-2xl font-bold">{formatCurrency(summaryStats.toplam60)}</p>
              </div>
              <div className="text-right">
                <p className="text-2xl font-bold opacity-90">
                  %{summaryStats.toplamAlacak > 0 ? ((summaryStats.toplam60 / summaryStats.toplamAlacak) * 100).toFixed(0) : 0}
                </p>
              </div>
            </div>
          </div>
          <div className="bg-gradient-to-r from-pink-500 to-pink-600 rounded-lg shadow-sm p-4 text-white">
            <div className="flex justify-between items-start">
              <div>
                <p className="text-sm opacity-90">80 Dakika</p>
                <p className="text-2xl font-bold">{formatCurrency(summaryStats.toplam80)}</p>
              </div>
              <div className="text-right">
                <p className="text-2xl font-bold opacity-90">
                  %{summaryStats.toplamAlacak > 0 ? ((summaryStats.toplam80 / summaryStats.toplamAlacak) * 100).toFixed(0) : 0}
                </p>
              </div>
            </div>
          </div>
          <div className="bg-gradient-to-r from-orange-500 to-orange-600 rounded-lg shadow-sm p-4 text-white">
            <div className="flex justify-between items-start">
              <div>
                <p className="text-sm opacity-90">120 Dakika</p>
                <p className="text-2xl font-bold">{formatCurrency(summaryStats.toplam120)}</p>
              </div>
              <div className="text-right">
                <p className="text-2xl font-bold opacity-90">
                  %{summaryStats.toplamAlacak > 0 ? ((summaryStats.toplam120 / summaryStats.toplamAlacak) * 100).toFixed(0) : 0}
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Graphs Section */}
        {showGraphs && (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
            <div className="bg-white rounded-xl shadow-sm p-6">
              <h3 className="text-lg font-semibold text-gray-800 mb-4">En Çok Kazanan 10 Şirket</h3>
              <div className="space-y-3">
                {getGraphData.top10.map((item, index) => (
                  <div key={index} className="flex items-center gap-3">
                    <span className="text-sm font-medium text-gray-600 w-8">{index + 1}.</span>
                    <div className="flex-1">
                      <div className="flex justify-between items-center mb-1">
                        <span className="text-sm font-medium text-gray-700">{item.name}</span>
                        <span className="text-sm font-bold text-gray-900">{formatCurrency(item.value)}</span>
                      </div>
                      <div className="w-full bg-gray-200 rounded-full h-2">
                        <div 
                          className="bg-indigo-600 h-2 rounded-full transition-all duration-500"
                          style={{ width: `${(item.value / getGraphData.top10[0]?.value || 1) * 100}%` }}
                        />
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className="bg-white rounded-xl shadow-sm p-6">
              <h3 className="text-lg font-semibold text-gray-800 mb-4">Ders Süresine Göre Dağılım</h3>
              <div className="space-y-3">
                {getGraphData.durationData.map((item, index) => {
                  const colors = ['bg-blue-500', 'bg-purple-500', 'bg-pink-500', 'bg-orange-500'];
                  const maxValue = Math.max(...getGraphData.durationData.map(d => d.value));
                  
                  return (
                    <div key={index} className="flex items-center gap-3">
                      <span className="text-sm font-medium text-gray-600 w-20">{item.name}</span>
                      <div className="flex-1">
                        <div className="flex justify-between items-center mb-1">
                          <div className="w-full bg-gray-200 rounded-full h-6 relative">
                            <div 
                              className={`${colors[index]} h-6 rounded-full transition-all duration-500 flex items-center justify-end pr-2`}
                              style={{ width: `${maxValue > 0 ? (item.value / maxValue) * 100 : 0}%` }}
                            >
                              <span className="text-xs text-white font-bold">{formatCurrency(item.value)}</span>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        )}

        {/* Bulk Actions */}
        {selectedSirketler.size > 0 && (
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
            <div className="flex items-center justify-between">
              <p className="text-sm text-blue-800">
                <span className="font-semibold">{selectedSirketler.size}</span> Şirket seçildi
              </p>
              <div className="flex gap-2">
                <button
                  onClick={exportSelectedToExcel}
                  className="px-3 py-1.5 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors text-sm flex items-center gap-1"
                >
                  <FiFileText className="w-4 h-4" />
                  Detaylı Excel
                </button>
                <button
                  onClick={sendBulkEmail}
                  className="px-3 py-1.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-sm flex items-center gap-1"
                >
                  <FiMail className="w-4 h-4" />
                  Toplu Email
                </button>
                <button
                  onClick={() => setSelectedSirketler(new Set())}
                  className="px-3 py-1.5 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors text-sm"
                >
                  İptal
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Report Table */}
        <div className="bg-white rounded-xl shadow-sm p-6">
          <h3 className="text-lg font-semibold text-gray-800 mb-4">
            {getPeriodText()} - {reportType === 'all' ? 'Tüm Şirketler' : 
                                 reportType === 'with-payment' ? 'Alacağı Olanlar' : 'Alacağı Olmayanlar'}
          </h3>
          
          {loading ? (
            <div className="flex justify-center items-center py-12">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-500"></div>
            </div>
          ) : filteredSirketler.length > 0 ? (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-gray-200">
                    <th className="text-center py-3 px-2">
                      <input
                        type="checkbox"
                        checked={selectedSirketler.size === filteredSirketler.length}
                        onChange={selectAllSirketler}
                        className="rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                      />
                    </th>
                    <th 
                      className="text-left py-3 px-4 font-semibold text-gray-700 cursor-pointer hover:bg-gray-50"
                      onClick={() => handleSort('id')}
                    >
                      <div className="flex items-center gap-1">
                        ID
                        {sortConfig.key === 'id' && (
                          sortConfig.direction === 'asc' ? <FiChevronUp className="w-4 h-4" /> : <FiChevronDown className="w-4 h-4" />
                        )}
                      </div>
                    </th>
                    <th 
                      className="text-left py-3 px-4 font-semibold text-gray-700 cursor-pointer hover:bg-gray-50"
                      onClick={() => handleSort('sirket_adi')}
                    >
                      <div className="flex items-center gap-1">
                        Şirket Adı
                        {sortConfig.key === 'sirket_adi' && (
                          sortConfig.direction === 'asc' ? <FiChevronUp className="w-4 h-4" /> : <FiChevronDown className="w-4 h-4" />
                        )}
                      </div>
                    </th>
                    <th 
                      className="text-center py-3 px-4 font-semibold text-gray-700 cursor-pointer hover:bg-gray-50"
                      onClick={() => handleSort('ogrenciSayisi')}
                    >
                      <div className="flex items-center justify-center gap-1">
                        Öğrenci
                        {sortConfig.key === 'ogrenciSayisi' && (
                          sortConfig.direction === 'asc' ? <FiChevronUp className="w-4 h-4" /> : <FiChevronDown className="w-4 h-4" />
                        )}
                      </div>
                    </th>
                    <th 
                      className="text-center py-3 px-4 font-semibold text-gray-700 cursor-pointer hover:bg-gray-50"
                      onClick={() => handleSort('kursSayisi')}
                    >
                      <div className="flex items-center justify-center gap-1">
                        Kurs
                        {sortConfig.key === 'kursSayisi' && (
                          sortConfig.direction === 'asc' ? <FiChevronUp className="w-4 h-4" /> : <FiChevronDown className="w-4 h-4" />
                        )}
                      </div>
                    </th>
                    <th 
                      className="text-center py-3 px-4 font-semibold text-gray-700 cursor-pointer hover:bg-gray-50"
                      onClick={() => handleSort('dersSayisi')}
                    >
                      <div className="flex items-center justify-center gap-1">
                        Ders
                        {sortConfig.key === 'dersSayisi' && (
                          sortConfig.direction === 'asc' ? <FiChevronUp className="w-4 h-4" /> : <FiChevronDown className="w-4 h-4" />
                        )}
                      </div>
                    </th>
                    <th 
                      className="text-right py-3 px-4 font-semibold text-gray-700 cursor-pointer hover:bg-gray-50"
                      onClick={() => handleSort('toplamAlacak')}
                    >
                      <div className="flex items-center justify-end gap-1">
                        {getPeriodText()} Alacağı
                        {sortConfig.key === 'toplamAlacak' && (
                          sortConfig.direction === 'asc' ? <FiChevronUp className="w-4 h-4" /> : <FiChevronDown className="w-4 h-4" />
                        )}
                      </div>
                    </th>
                    <th className="text-center py-3 px-4 font-semibold text-gray-700">Durum</th>
                    <th className="text-center py-3 px-4 font-semibold text-gray-700">İşlemler</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {filteredSirketler.map((sirket) => {
                    const periodAmount = getSelectedPeriodAmount(sirket);
                    const periodDetails = getSelectedPeriodDetails(sirket);
                    const isExpanded = expandedRows.has(sirket.id);
                    const isSelected = selectedSirketler.has(sirket.id);
                    
                    return (
                      <React.Fragment key={sirket.id}>
                        <tr className={`transition-colors ${isSelected ? 'bg-blue-50' : 'hover:bg-gray-50'}`}>
                          <td className="text-center py-3 px-2">
                            <input
                              type="checkbox"
                              checked={isSelected}
                              onChange={() => toggleSirketSelection(sirket.id)}
                              className="rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                            />
                          </td>
                          <td className="py-3 px-4 text-sm text-gray-600">{sirket.id}</td>
                          <td className="py-3 px-4">
                            <div>
                              <p className="font-medium text-gray-800">{sirket.sirket_adi}</p>
                              <p className="text-xs text-gray-500">
                                {sirket.aktifOgrenciSayisi} aktif / {sirket.pasifOgrenciSayisi} pasif
                              </p>
                            </div>
                          </td>
                          <td className="text-center py-3 px-4">
                            <span className="inline-flex items-center justify-center w-8 h-8 bg-indigo-100 text-indigo-700 rounded-full font-semibold text-sm">
                              {sirket.ogrenciSayisi || 0}
                            </span>
                          </td>
                          <td className="text-center py-3 px-4">
                            <span className="inline-flex items-center justify-center w-8 h-8 bg-purple-100 text-purple-700 rounded-full font-semibold text-sm">
                              {sirket.kursSayisi || 0}
                            </span>
                          </td>
                          <td className="text-center py-3 px-4">
                            <span className="inline-flex items-center justify-center w-8 h-8 bg-blue-100 text-blue-700 rounded-full font-semibold text-sm">
                              {periodDetails.length}
                            </span>
                          </td>
                          <td className="text-right py-3 px-4">
                            <span className={`font-bold ${periodAmount > 0 ? 'text-green-600' : 'text-gray-400'}`}>
                              {formatCurrency(periodAmount)}
                            </span>
                          </td>
                          <td className="text-center py-3 px-4">
                            {periodAmount > 0 ? (
                              <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                                Alacaklı
                              </span>
                            ) : (
                              <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
                                Alacak Yok
                              </span>
                            )}
                          </td>
                          <td className="text-center py-3 px-4">
                            <div className="flex items-center justify-center gap-1">
                              {periodDetails.length > 0 && (
                                <button
                                  onClick={() => toggleRowExpansion(sirket.id)}
                                  className="inline-flex items-center gap-1 px-2 py-1 bg-gray-100 text-gray-700 rounded hover:bg-gray-200 transition-colors text-sm font-medium"
                                >
                                  {isExpanded ? <FiChevronUp className="w-4 h-4" /> : <FiChevronDown className="w-4 h-4" />}
                                </button>
                              )}
                              <button
                                onClick={() => handleSirketClick(sirket)}
                                className="inline-flex items-center gap-1 px-3 py-1.5 bg-indigo-100 text-indigo-700 rounded-lg hover:bg-indigo-200 transition-colors text-sm font-medium"
                                disabled={periodDetails.length === 0}
                              >
                                <FiEye className="w-4 h-4" />
                                Detay
                              </button>
                            </div>
                          </td>
                        </tr>
                        {isExpanded && (
                          <tr>
                            <td colSpan={9} className="bg-gray-50 px-4 py-3">
                              <div className="text-sm">
                                <h4 className="font-semibold text-gray-700 mb-3">Ders Detayları</h4>
                                <div className="overflow-x-auto">
                                  <table className="w-full">
                                    <thead className="bg-white">
                                      <tr className="border-b border-gray-200">
                                        <th className="text-left py-2 px-3 text-xs font-semibold text-gray-600">Tarih</th>
                                        <th className="text-left py-2 px-3 text-xs font-semibold text-gray-600">Öğrenci</th>
                                        <th className="text-left py-2 px-3 text-xs font-semibold text-gray-600">Öğretmen</th>
                                        <th className="text-left py-2 px-3 text-xs font-semibold text-gray-600">Konu</th>
                                        <th className="text-center py-2 px-3 text-xs font-semibold text-gray-600">Süre</th>
                                        <th className="text-right py-2 px-3 text-xs font-semibold text-gray-600">Üret</th>
                                      </tr>
                                    </thead>
                                    <tbody className="divide-y divide-gray-100">
                                      {periodDetails.slice(-5).reverse().map((ders, index) => (
                                        <tr key={index} className="hover:bg-gray-50">
                                          <td className="py-2 px-3 text-xs text-gray-600">
                                            {formatDate(ders.tarih)}
                                          </td>
                                          <td className="py-2 px-3">
                                            <p className="text-xs font-medium text-gray-800">{ders.ogrenciAdi}</p>
                                            {ders.ogrenciTelefon !== '-' && (
                                              <p className="text-xs text-gray-500">{ders.ogrenciTelefon}</p>
                                            )}
                                          </td>
                                          <td className="py-2 px-3">
                                            <p className="text-xs font-medium text-gray-800">{ders.ogretmenAdi}</p>
                                          </td>
                                          <td className="py-2 px-3 text-xs text-gray-600">
                                            {ders.konu || 'Konu yok'}
                                          </td>
                                          <td className="text-center py-2 px-3">
                                            <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                                              {ders.sure} dk
                                            </span>
                                          </td>
                                          <td className="text-right py-2 px-3">
                                            <span className="text-xs font-semibold text-green-600">
                                              {formatCurrency(ders.ucret)}
                                            </span>
                                          </td>
                                        </tr>
                                      ))}
                                    </tbody>
                                  </table>
                                </div>
                              </div>
                            </td>
                          </tr>
                        )}
                      </React.Fragment>
                    );
                  })}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="text-center py-8 text-gray-500">
              <FiBriefcase className="w-12 h-12 mx-auto mb-3 text-gray-400" />
              <p>Gösterilecek veri bulunamadı</p>
              {searchTerm && (
                <p className="text-sm mt-2">"{searchTerm}" için sonuç bulunamadı</p>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Details Modal */}
      {showDetailsModal && selectedSirket && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-hidden">
            <div className="p-6 border-b border-gray-200">
              <div className="flex justify-between items-center">
                <div>
                  <h2 className="text-xl font-bold text-gray-800">Şirket Payı Detayları</h2>
                  <p className="text-gray-600 mt-1">{selectedSirket.sirket_adi} - {getPeriodText()}</p>
                  <div className="flex gap-4 mt-2 text-sm">
                    <span className="text-gray-600">Toplam Ders: <strong>{getSelectedPeriodDetails(selectedSirket).length}</strong></span>
                    <span className="text-gray-600">Toplam Alacak: <strong className="text-green-600">{formatCurrency(getSelectedPeriodAmount(selectedSirket))}</strong></span>
                  </div>
                </div>
                <button
                  onClick={() => setShowDetailsModal(false)}
                  className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                >
                  <svg className="w-6 h-6 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
            </div>
            
            <div className="p-6 overflow-y-auto max-h-[60vh]">
              {getSelectedPeriodDetails(selectedSirket).length > 0 ? (
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b border-gray-200">
                        <th className="text-left py-3 px-4 font-semibold text-gray-700">Ders ID</th>
                        <th className="text-left py-3 px-4 font-semibold text-gray-700">Öğrenci</th>
                        <th className="text-left py-3 px-4 font-semibold text-gray-700">Öğretmen</th>
                        <th className="text-left py-3 px-4 font-semibold text-gray-700">Konu</th>
                        <th className="text-left py-3 px-4 font-semibold text-gray-700">Başlangıç Tarihi</th>
                        <th className="text-center py-3 px-4 font-semibold text-gray-700">Ders Süresi</th>
                        <th className="text-right py-3 px-4 font-semibold text-gray-700">Şirket Payı</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200">
                      {getSelectedPeriodDetails(selectedSirket).map((ders) => (
                        <tr key={ders.id} className="hover:bg-gray-50">
                          <td className="py-3 px-4 text-sm text-gray-600">{ders.id}</td>
                          <td className="py-3 px-4">
                            <div>
                              <p className="text-sm font-medium text-gray-900">{ders.ogrenciAdi}</p>
                              {ders.ogrenciTelefon !== '-' && (
                                <p className="text-xs text-gray-500">{ders.ogrenciTelefon}</p>
                              )}
                            </div>
                          </td>
                          <td className="py-3 px-4">
                            <div>
                              <p className="text-sm font-medium text-gray-900">{ders.ogretmenAdi}</p>
                              {ders.ogretmenTelefon !== '-' && (
                                <p className="text-xs text-gray-500">{ders.ogretmenTelefon}</p>
                              )}
                            </div>
                          </td>
                          <td className="py-3 px-4 text-sm">{ders.konu || '-'}</td>
                          <td className="py-3 px-4 text-sm">{formatDate(ders.tarih)}</td>
                          <td className="text-center py-3 px-4">
                            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                              {ders.sure} dk
                            </span>
                          </td>
                          <td className="text-right py-3 px-4 font-medium text-green-600">
                            {formatCurrency(ders.ucret)}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                    <tfoot className="border-t-2 border-gray-300 bg-gray-50">
                      <tr>
                        <td colSpan="6" className="py-3 px-4 font-bold text-gray-700">
                          Toplam
                        </td>
                        <td className="text-right py-3 px-4 font-bold text-green-700">
                          {formatCurrency(getSelectedPeriodAmount(selectedSirket))}
                        </td>
                      </tr>
                    </tfoot>
                  </table>
                </div>
              ) : (
                <div className="text-center py-8 text-gray-500">
                  Bu dönemde ders kaydı bulunmamaktadır.
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Print Styles */}
      <style jsx>{`
        @media print {
          .no-print {
            display: none !important;
          }
          
          @page {
            margin: 1cm;
          }
          
          table {
            font-size: 12px;
          }
          
          .bg-gradient-to-r {
            background: #f3f4f6 !important;
            color: #1f2937 !important;
          }
        }
      `}</style>
    </div>
  );
};

export default CompaniesReportPage;
